import React, { Fragment } from "react";
import { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import { IAuthProps, WellKnownGroups } from "../AuthContext";
import DelayInput from "../DelayInput";

import { V2 } from "../../Api";
import { formatName } from "../../Utilities";
import withAuth from "../withAuth";
import withTitle, { IViewTitleProps } from "../withTitle";
import TabControl from "../TabControl";

type DataMode = "appointments" | "completion" | "booking" | "outreach";

interface IMetricsMarketingViewState {
    isLoading: boolean | string;
    hasChanged: boolean;
    searchStart: string;
    searchEnd: string;
    searchMonth: string;
    dateType: "month" | "range" | "week";
    dataMode: DataMode;
    selectedTabIndex: number;

    employees: any[];
    completionStats: any[];
    fairStats: any[];
    outreachStats: any[];

    group: string;

    [name: string]: any;
}

interface IMetricsMarketingViewProps extends IAuthProps, RouteComponentPropsHistory<any, any>, IViewTitleProps {

}


class MetricsMarketingView extends React.Component<IMetricsMarketingViewProps, IMetricsMarketingViewState> {
    constructor(props: Readonly<IMetricsMarketingViewProps>) {
        super(props);

        const today = DateTime.local();

        const state: IMetricsMarketingViewState = {
            isLoading: true,
            hasChanged: false,
            searchStart: props.location.state?.searchStart || today.startOf("month").toISODate(),
            searchEnd: props.location.state?.searchEnd || today.endOf("month").toISODate(),
            searchMonth: props.location.state?.searchMonth || today.startOf("month").toFormat("yyyy-MM"),
            dateType: props.location.state?.dateType || "month",
            dataMode: props.location.state?.dataMode || "appointments",
            selectedTabIndex: props.location.state?.selectedTabIndex || 0,

            employees: [],
            completionStats: [],
            fairStats: [],
            outreachStats: [],

            group: this.groupNameToInfo(props.match.params.group).id,
        };

        this.state = state;

        this.search = this.search.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.handleTabChanged = this.handleTabChanged.bind(this);
    }

    public componentDidMount() {
        this.search();

        const { title } = this.groupNameToInfo(this.props.match.params.group);
        window.setTimeout(() => { this.props.viewTitleContext.changeTitle(title, "both") }, 0);
    }

    public componentDidUpdate(prevProps: Readonly<IMetricsMarketingViewProps>, prevState: Readonly<IMetricsMarketingViewState>) {
        if (this.state.searchStart !== prevState.searchStart && !this.state.searchStart) {
            // Default back to safe value
            this.setState({
                searchStart: DateTime.local().startOf("month").toISODate()
            }, () => this.search());
        }
        else if (this.state.searchEnd !== prevState.searchEnd && !this.state.searchEnd) {
            // Default back to safe value
            this.setState({
                searchEnd: DateTime.local().endOf("month").toISODate()
            }, () => this.search());
        }
        else if (this.state.searchMonth !== prevState.searchMonth && !this.state.searchMonth) {
            // Default back to safe value
            this.setState({
                searchMonth: DateTime.local().startOf("month").toFormat("yyyy-MM")
            }, () => this.search());
        }

        if (this.props.match.params.group !== prevProps.match.params.group) {
            // Group changed
            const info = this.groupNameToInfo(this.props.match.params.group);
            this.setState({
                group: info.id,
                selectedTabIndex: 0,
                dataMode: "appointments"
            }, () => { this.props.viewTitleContext.changeTitle(info.title, "both"); this.search(); });
        }
    }

    public render() {
        const showSurveys = this.state.group === WellKnownGroups.HfTeam;
        return (
            <div className="viewer-root">
                <div className="filter-control-box">
                    <select name="dateType" value={this.state.dateType} onChange={this.handleElementChange}>
                        <option value="month">Month</option>
                        <option value="week">Week</option>
                        <option value="range">Date Range</option>
                    </select>
                    {
                        this.state.dateType === "range"
                            ? <Fragment>
                                <div className="control-group fill-space">
                                    <span>Start Date:</span>
                                    <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                                </div>
                                <div className="control-group fill-space">
                                    <span>End Date:</span>
                                    <DelayInput type="date" timeout={500} value={this.state.searchEnd} name="searchEnd" onChangeDelayed={this.handleElementChange} />
                                </div>
                            </Fragment>
                            : this.state.dateType === "week"
                                ? <div className="control-group">
                                    <span>Week Of:</span>
                                    <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                                </div>
                                : <DelayInput type="month" className="fill-space" timeout={500} value={this.state.searchMonth} name="searchMonth" onChangeDelayed={this.handleElementChange} />
                    }
                </div>
                <TabControl headers={showSurveys ? ["Appointments", "Completion", "Fair Booking"] : ["Appointments", "Completion", "Outreach"]} onTabChanged={this.handleTabChanged} selectedIndex={this.state.selectedTabIndex}>
                    {this.renderAppointmentsTab(showSurveys)}
                    {this.renderAppointmentCompletion()}
                    {showSurveys
                        ? this.renderFairBooking()
                        : this.renderOutreach()}
                </TabControl>
            </div>
        );
    }

    private renderAppointmentsTab(showSurveys: boolean) {
        return <div>
            <table className="table-vertical">
                <thead>
                    <tr>
                        <th colSpan={1} />
                        {showSurveys ? <th colSpan={5} className="col-smallest col-center">Surveys</th> : null}
                        <th colSpan={4} className="col-smallest col-center">Appointments</th>
                        <th colSpan={4} className="col-smallest col-center">Surgeries</th>
                    </tr>
                    <tr>
                        <th>Name</th>
                        {showSurveys ? <th className="col-smallest col-center th-font-tiny">Surveys<br />Collected</th> : null}
                        {showSurveys ? <th className="col-smallest col-center th-font-tiny">Female</th> : null}
                        {showSurveys ? <th className="col-smallest col-center th-font-tiny">Male</th> : null}
                        {showSurveys ? <th className="col-smallest col-center th-font-tiny">Private<br />Ins.</th> : null}
                        {showSurveys ? <th className="col-smallest col-center th-font-tiny">Govt.<br />Ins.</th> : null}
                        <th className="col-smallest col-center th-font-tiny">Outstanding</th>
                        <th className="col-smallest col-center th-font-tiny">Canceled<br />Noshow</th>
                        <th className="col-smallest col-center th-font-tiny">Completed</th>
                        <th className="col-smallest col-center col-bold th-font-tiny">Total<br />Sched.</th>

                        <th className="col-smallest col-center th-font-tiny">Outstanding</th>
                        <th className="col-smallest col-center th-font-tiny">Canceled</th>
                        <th className="col-smallest col-center th-font-tiny">Completed</th>
                        <th className="col-smallest col-center col-bold th-font-tiny">Total<br />Sched.</th>
                    </tr>
                </thead>
                <thead className="table-vertical-head-small">
                    <tr>
                        <th>Marketing Stats</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        this.state.isLoading
                            ? <tr>
                                <td colSpan={11}>
                                    {
                                        typeof this.state.isLoading === "string"
                                            ? <span className="error-text">{this.state.isLoading}</span>
                                            : <div className="spinner-center lds-dual-ring" />
                                    }
                                </td>
                            </tr>
                            : this.state.employees.length < 1
                                ? <tr>
                                    <td colSpan={11}>No data available</td>
                                </tr>
                                : this.state.employees.map((a, i) => (
                                    <tr key={a.id || a.employee.id} className={a.id === "total" ? "col-bold" : (a.isInGroup === false) ? "row-gray" : undefined}>
                                        <td className="inner-edge" data-caption="Name: ">{formatName(a.name || a.employee.name)}</td>

                                        {showSurveys ? <td className="col-center inline inner-edge" data-caption="Surveys collected: ">{a.surveyTotal || ""}</td> : null}
                                        {showSurveys ? <td className="col-center inline" data-caption="Female: ">{a.surveyFemale || ""}</td> : null}
                                        {showSurveys ? <td className="col-center inline inner-edge" data-caption="Male: ">{a.surveyMale || ""}</td> : null}
                                        {showSurveys ? <td className="col-center inline" data-caption="Surveys with private insurance: ">{a.surveyInsurancePrivate || ""}</td> : null}
                                        {showSurveys ? <td className="col-center inline inner-edge" data-caption="Surveys with private insurance: ">{a.surveyInsuranceNonPrivate || ""}</td> : null}

                                        <td className="col-center inline" data-caption="Outstanding appointments: ">{a.appointmentOutstanding || ""}</td>
                                        <td className="col-center inline" data-caption="Canceled/Noshow appointments: ">{a.appointmentCancel || ""}</td>
                                        <td className="col-center inline inner-edge" data-caption="Completed appointments: ">{a.appointmentComplete || ""}</td>
                                        <td className="col-center inline inner-edge" data-caption="Total scheduled appointments: ">{a.appointmentTotal}</td>

                                        <td className="col-center inline" data-caption="Outstanding surgeries: ">{a.surgeryPending || ""}</td>
                                        <td className="col-center inline" data-caption="Canceled surgeries: ">{a.surgeryCancel || ""}</td>
                                        <td className="col-center inline inner-edge" data-caption="Complete surgeries: ">{a.surgeryComplete || ""}</td>
                                        <td className="col-center inline inner-edge" data-caption="Total scheduled surgeries: ">{a.surgeryTotal}</td>
                                    </tr>
                                ))
                    }
                </tbody>
            </table>
            <p>
                <em>
                    <small>
                        Notes: This displays appointments based on the date they were created on.<br />
                        The survey total is the number of questionnaires submitted at fairs the marketer was at during the date range selected<br />
                        The sum of the survey total column may be greater than the total number of surveys collected, as surveys from health fairs with multiple marketers are counted for each marketer.
                    </small>
                </em>
            </p>
        </div>
    }

    private renderAppointmentCompletion() {
        return <div>
            <table className="table-vertical">
                <thead>
                    <tr>
                        <th colSpan={4} className="col-center">Appointments</th>
                    </tr>
                    <tr>
                        <th>Name</th>
                        <th className="col-smallest col-center th-font-tiny">Total</th>
                        <th className="col-smallest col-center th-font-tiny">Completed</th>
                        <th className="col-smallest col-center th-font-tiny">Outstanding</th>
                    </tr>
                </thead>
                <thead className="table-vertical-head-small">
                    <tr>
                        <th>Appointment completion stats</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        this.state.isLoading
                            ? <tr>
                                <td colSpan={4}>
                                    {
                                        typeof this.state.isLoading === "string"
                                            ? <span className="error-text">{this.state.isLoading}</span>
                                            : <div className="spinner-center lds-dual-ring" />
                                    }
                                </td>
                            </tr>
                            : this.state.completionStats.length < 1
                                ? <tr>
                                    <td colSpan={4}>No data available</td>
                                </tr>
                                : this.state.completionStats.map((a, i) => (
                                    <tr key={a.employee.id} className={(a.isInGroup === false) ? "row-gray" : undefined}>
                                        <td className="inner-edge" data-caption="Name: ">{formatName(a.employee.name)}</td>
                                        <td className="col-center inline inner-edge" data-caption="Total appointments: ">{a.total || ""}</td>
                                        <td className="col-center inline" data-caption="Completed appointments: ">{a.completed || ""} {a.completed ? `(${(a.completed * 100 / a.total).toFixed(0)} %)` : ""}</td>
                                        <td className="col-center inline" data-caption="Outstanding appointments: ">{a.outstanding || ""}</td>
                                    </tr>
                                ))
                    }
                </tbody>
            </table>
            <p>
                <em>
                    <small>
                        Notes: This counts office appointments based on the date they were scheduled for (date of service).
                    </small>
                </em>
            </p>
        </div>;
    }

    private renderFairBooking() {
        return <div>
            <table className="table-vertical">
                <thead>
                    <tr>
                        <th colSpan={4} className="col-center">Fairs Booked</th>
                    </tr>
                    <tr>
                        <th>Name</th>
                        <th className="col-smallest col-center th-font-tiny">Total</th>
                        <th className="col-smallest col-center th-font-tiny">&gt;= 20<br />est.</th>
                    </tr>
                </thead>
                <thead className="table-vertical-head-small">
                    <tr>
                        <th>Fair booking stats</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        this.state.isLoading
                            ? <tr>
                                <td colSpan={4}>
                                    {
                                        typeof this.state.isLoading === "string"
                                            ? <span className="error-text">{this.state.isLoading}</span>
                                            : <div className="spinner-center lds-dual-ring" />
                                    }
                                </td>
                            </tr>
                            : this.state.fairStats.length < 1
                                ? <tr>
                                    <td colSpan={4}>No data available</td>
                                </tr>
                                : this.state.fairStats.map((a, i) => (
                                    <tr key={a.employee.id} className={`${(a.employee.id === "total" ? "col-bold" : "clickable")} ${(a.isInGroup === false) ? "row-gray" : ""}`} onClick={() => this.goFairBooking(a.employee.id)}>
                                        <td className="inner-edge" data-caption="Name: ">{formatName(a.employee.name)}</td>
                                        <td className="col-center inline" data-caption="Total Booked: ">{a.total || ""}</td>
                                        <td className="col-center inline" data-caption="Total Booked >= 20: ">{a.total20 || ""}</td>
                                    </tr>
                                ))
                    }
                </tbody>
            </table>
            <p>
                <em>
                    <small>
                        Note: This counts health fairs that were created by the listed people, on the days within the range selected.
                    </small>
                </em>
            </p>
        </div>;
    }

    private renderOutreach() {
        return <div>
            <table className="table-vertical">
                <thead>
                    <tr>
                        <th colSpan={10} className="col-center">Outreach</th>
                    </tr>
                    <tr>
                        <th>Name</th>
                        <th className="col-smallest col-center th-font-tiny">Unique<br />Surveys</th>
                        <th className="col-smallest col-center th-font-tiny">Total</th>
                        <th className="col-smallest col-center th-font-tiny">Didn't<br />Reach</th>
                        <th className="col-smallest col-center th-font-tiny">Reached<br />Didn't Want.</th>
                        <th className="col-smallest col-center th-font-tiny">Call<br />Later</th>
                        <th className="col-smallest col-center th-font-tiny">Text<br />Sent</th>
                        <th className="col-smallest col-center th-font-tiny">Made<br />Appt.</th>
                        <th className="col-smallest col-center th-font-tiny">Do Not<br />Contact</th>
                        <th className="col-smallest col-center th-font-tiny">Not<br />Needed</th>
                    </tr>
                </thead>
                <thead className="table-vertical-head-small">
                    <tr>
                        <th>Outreach stats</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        this.state.isLoading
                            ? <tr>
                                <td colSpan={10}>
                                    {
                                        typeof this.state.isLoading === "string"
                                            ? <span className="error-text">{this.state.isLoading}</span>
                                            : <div className="spinner-center lds-dual-ring" />
                                    }
                                </td>
                            </tr>
                            : this.state.outreachStats.length < 1
                                ? <tr>
                                    <td colSpan={10}>No data available</td>
                                </tr>
                                : this.state.outreachStats.map((a, i) => (
                                    <tr key={a.employee.id} className={`${(a.isInGroup === false) ? "row-gray" : ""}`}>
                                        <td className="inner-edge" data-caption="Name: ">{formatName(a.employee.name)}</td>
                                        <td className="col-center inline inner-edge" data-caption="Unique Surveys: ">{a.uniqueSurveys || ""}</td>
                                        <td className="col-center inline inner-edge" data-caption="Total: ">{a.total || ""}</td>
                                        <td className="col-center inline" data-caption="Not Reached: ">{a.notReached || ""}</td>
                                        <td className="col-center inline" data-caption="Reached: ">{a.reached || ""}</td>
                                        <td className="col-center inline" data-caption="Call Later: ">{a.callLater || ""}</td>
                                        <td className="col-center inline" data-caption="Text Sent: ">{a.textSent || ""}</td>
                                        <td className="col-center inline inner-edge" data-caption="Made Appointment: ">{a.madeAppointment || ""}</td>
                                        <td className="col-center inline" data-caption="Do Not Contact: ">{a.doNotContact || ""}</td>
                                        <td className="col-center inline" data-caption="Not Needed: ">{a.notNeeded || ""}</td>
                                    </tr>
                                ))
                    }
                </tbody>
            </table>
            <p>
                <em>
                    <small>
                        Note: This counts each instance of outreaching a patient. If a single patient was reached out to twice, it will count it twice.<br />
                        Unique Surveys are the number of surveys that were reached out to by the person, regardless of if they reached out more than once.<br />
                        Gray rows are people who aren't currently assigned to the group in the system.
                    </small>
                </em>
            </p>
        </div>;
    }

    private async search() {
        try {
            if (!this.state.searchStart || !this.state.searchEnd) {
                this.setState({
                    employees: [],
                    completionStats: [],
                    isLoading: false,
                });
                return;
            }

            this.saveSearchState();

            this.setState({
                isLoading: true
            });

            let after;
            let before;
            if (this.state.dateType === "month") {
                after = DateTime.fromISO(this.state.searchMonth).startOf("month");
                before = after.endOf("month");
            }
            else if (this.state.dateType === "week") {
                after = DateTime.fromISO(this.state.searchStart).plus({ days: 1 }).startOf("week").minus({ days: 1 });
                before = after.plus({ days: 7 }).endOf("day");
            }
            else {
                after = DateTime.fromISO(this.state.searchStart).startOf("day");
                before = DateTime.fromISO(this.state.searchEnd).endOf("day");
            }

            const parameters = [
                `after=${after.toISO()}`,
                `before=${before.toISO()}`,
                `group=${this.state.group}`
            ];

            if (this.state.dataMode === "appointments") {
                const data = await V2.get<any[]>(`metrics/marketing?${parameters.join("&")}`);

                const sumRow = {
                    id: "total",
                    name: { first: "Total", last: "" },
                    surveyTotal: 0,
                    surveyFemale: 0,
                    surveyMale: 0,
                    appointmentOutstanding: 0,
                    appointmentCancel: 0,
                    appointmentComplete: 0,
                    appointmentTotal: 0,
                    surgeryPending: 0,
                    surgeryCancel: 0,
                    surgeryComplete: 0,
                    surgeryTotal: 0,
                    isInGroup: true
                };

                data.forEach(a => {
                    if (!a.isInGroup) {
                        return;
                    }

                    a.appointmentOutstanding = (a.appointmentPending || 0) + (a.appointmentActive || 0);

                    sumRow.surveyTotal += a.surveyTotal || 0;
                    sumRow.surveyFemale += a.surveyFemale || 0;
                    sumRow.surveyMale += a.surveyMale || 0;
                    sumRow.appointmentOutstanding += a.appointmentOutstanding || 0;
                    sumRow.appointmentCancel += a.appointmentCancel || 0;
                    sumRow.appointmentComplete += a.appointmentComplete || 0;
                    sumRow.appointmentTotal += a.appointmentTotal || 0;
                    sumRow.surgeryPending += a.surgeryPending || 0;
                    sumRow.surgeryCancel += a.surgeryCancel || 0;
                    sumRow.surgeryComplete += a.surgeryComplete || 0;
                    sumRow.surgeryTotal += a.surgeryTotal || 0;
                });

                data.push(sumRow);

                data.sort((a, b) => a.id === "total" ? 1 : formatName(a.name || a.employee.name).localeCompare(formatName(b.name || b.employee.name)));

                this.setState({
                    employees: data.filter(a => a.isInGroup),
                    isLoading: false,
                });
            }
            else if (this.state.dataMode === "completion") {
                const data = await V2.get<any[]>(`metrics/marketing/completion?${parameters.join("&")}`);

                data.sort((a, b) => formatName(a.employee.name).localeCompare(formatName(b.employee.name)));

                this.setState({
                    completionStats: data.filter(a => a.isInGroup),
                    isLoading: false,
                });
            }
            else if (this.state.dataMode === "booking") {
                const data = await V2.get<any[]>(`metrics/marketing/fair-booking?${parameters.join("&")}`);

                data.sort((a, b) => formatName(a.employee.name).localeCompare(formatName(b.employee.name)));

                // Calculate totals
                const totalRow = {
                    employee: {
                        id: "total",
                        name: {
                            first: "Total",
                            last: ""
                        }
                    },
                    total: 0,
                    total20: 0,
                    isInGroup: true,
                };

                for (const row of data) {
                    totalRow.total += row.total;
                    totalRow.total20 += row.total20;
                }

                data.push(totalRow);

                this.setState({
                    fairStats: data.filter(a => a.isInGroup),
                    isLoading: false,
                });
            }
            else if (this.state.dataMode === "outreach") {
                const data = await V2.get<any[]>(`metrics/marketing/outreach?${parameters.join("&")}`);

                data.sort((a, b) => (b.isInGroup - a.isInGroup) || formatName(a.employee.name).localeCompare(formatName(b.employee.name)));

                this.setState({
                    outreachStats: data,
                    isLoading: false,
                });
            }
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        }, () => this.search());
    }

    private handleTabChanged(tabIndex: number) {
        let mode: DataMode = "appointments";
        const showSurveys = this.state.group === WellKnownGroups.HfTeam;

        switch (tabIndex) {
            case 0: mode = "appointments"; break;
            case 1: mode = "completion"; break;
            case 2: mode = showSurveys
                ? "booking"
                : "outreach"; break;
            default: mode = "appointments"; break;
        }

        this.setState({
            dataMode: mode,
            selectedTabIndex: tabIndex
        }, () => this.search());
    }

    private groupNameToInfo(name: string) {
        switch (name) {
            case "hf": return { id: WellKnownGroups.HfTeam, title: "HF Team Stats" };
            case "telemarketer": return { id: WellKnownGroups.PatientCareRep, title: "Patient Care Rep. Stats" };
            case "surgical": return { id: WellKnownGroups.SurgicalConsultant, title: "Surgical Consultant Stats" };
            case "appointmentspecialist": return { id: WellKnownGroups.AppointmentSpecialist, title: "Appointment Specialist Stats" };
            default: return { id: "", title: "Marketing Stats" };
        }
    }

    private goFairBooking(id: string) {
        if (id === "total") {
            return;
        }

        let after: DateTime;
        let before: DateTime;

        if (this.state.dateType === "month") {
            after = DateTime.fromISO(this.state.searchMonth).startOf("month");
            before = after.endOf("month").plus({ days: 1 }).startOf("day");
        }
        else if (this.state.dateType === "week") {
            after = DateTime.fromISO(this.state.searchStart).plus({ days: 1 }).startOf("week").minus({ days: 1 });
            before = after.plus({ days: 7 }).startOf("day");
        }
        else {
            after = DateTime.fromISO(this.state.searchStart);
            before = DateTime.fromISO(this.state.searchEnd).plus({ days: 1 }).startOf("day");
        }

        this.props.history.push(`/metrics/marketing/hf/fair-booking/${id}/${after.toISODate()}/${before.toISODate()}`);
    }

    private saveSearchState() {
        const currentLocation = this.props.location;
        currentLocation.state = {
            dateType: this.state.dateType,
            dataMode: this.state.dataMode,
            selectedTabIndex: this.state.selectedTabIndex,
            searchStart: this.state.searchStart,
            searchEnd: this.state.searchEnd,
            searchMonth: this.state.searchMonth,
        };
        this.props.history.replace(currentLocation);
    }
}

export default withTitle("Marketing Stats", withAuth(MetricsMarketingView));
